import { Close } from "@mui/icons-material";
import { alpha, IconButton, Theme } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import React, { PropsWithChildren, ReactElement } from "react";
import { Toast } from "./Toast";

interface Props {
    open: boolean;
    title: string;
    error?: string;
    disabled?: boolean;
    firstTitle?: string;
    onFirst?: () => void;
    secondTitle?: string;
    onSecond?: () => void;
    thirdTitle?: string;
    onThird?: () => void;
    errorClassName?: string;
    firstDisabled?: boolean;
    paperClassName?: string;
    secondDisabled?: boolean;
    thirdDisabled?: boolean;
    onCloseError?: () => void;
    backdropClassName?: string;
    testId?: string;
    onClose?: () => void;
    bottomChildren?: ReactElement;
    childrenGrow?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
    children: {
        display: "flex",
        flexDirection: "column",
        padding: "0.5rem 0.25rem",
        minHeight: 0
    },
    childrenGrow: {
        flexGrow: 1
    },
    childrenWrapper: {
        padding: "0 2rem",
        display: "flex",
        flexDirection: "column"
    },
    bottomChildrenWrapper: {
        display: "flex",
        flex: 1,
        flexDirection: "column"
    },
    backdrop: {
        backgroundColor: "rgba(0,0,0,0.25)"
    },
    paper: {
        margin: "-1rem",
        boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        maxWidth: "600px",
        width: "calc(100vw - 0.2rem)"
    },
    title: {
        fontFamily: theme.typography.h1.fontFamily,
        fontSize: "1.5rem",
        fontWeight: 500,
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
        flexGrow: 1
    },
    buttons: {
        margin: "0.5rem 1rem",
        justifyContent: "center"
    },
    disabledButton: {
        color: `${alpha(theme.palette.primary.contrastText, 0.6)}!important`
    },
    button: {
        margin: "0.25rem 0.5rem",
        fontFamily: theme.typography.button.fontFamily,
        fontWeight: theme.typography.button.fontWeight,
        textTransform: "none",
        padding: "0.5rem 1.5rem",
        transition: theme.transitions.create(["border", "color", "background-color"]),
        minWidth: "144px"
    },
    buttonSecondary: {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
        "&:hover": {
            backgroundColor: theme.palette.primary.dark,
        }
    },
    buttonPrimary: {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
        "&:hover": {
            backgroundColor: theme.palette.primary.light,
        }
    },
    titleWrapper: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        padding: "1rem 1.5rem 0 1.5rem",
        marginBottom: "0.5rem"
    },
    error: {
        margin: "calc(0.5rem + 3px)"
    },
    spacer: {
        width: "40px",
        height: "44px"
    }
}));

const PopupDialog: React.FC<PropsWithChildren<Props>> = props => {
    const classes = useStyles();

    return (
        <Dialog
            data-cy={`${props.testId}-Dialog`}
            disableEnforceFocus
            disableEscapeKeyDown // Allows listening for the escape key in the parent component
            BackdropProps={{ className: clsx(classes.backdrop, props.backdropClassName) }}
            PaperProps={{
                className: clsx(classes.paper, props.paperClassName),
                elevation: 16
            }}
            open={props.open}>

            <div className={classes.titleWrapper}>

                <div className={classes.spacer} />

                <Typography
                    variant="h2"
                    className={classes.title}>
                    {props.title}
                </Typography>

                {props.onClose ? (
                    <IconButton
                        data-cy={`${props.testId}-Button-Close`}
                        onClick={props.onClose}>
                        <Close />
                    </IconButton>
                ) : (
                    <div className={classes.spacer} />
                )}

            </div>

            <Toast
                testId={`${props.testId}-Toast`}
                message={props.error}
                onClose={props.onCloseError}
                className={clsx(props.error && classes.error, props.errorClassName)} />

            <DialogContent className={classes.childrenWrapper}>

                <div className={clsx(classes.children, props.childrenGrow && classes.childrenGrow)}>

                    {props.children}

                </div>

            </DialogContent>

            {(props.onFirst || props.onSecond) && (
                <DialogActions className={classes.bottomChildrenWrapper}>

                    {props.bottomChildren}

                    <div className={classes.buttons}>
                        {props.onThird && (
                            <Button
                                data-cy={`${props.testId}-Button-Third`}
                                disableElevation
                                classes={{
                                    disabled: classes.disabledButton
                                }}
                                className={clsx(classes.button, classes.buttonSecondary)}
                                onClick={props.onThird}
                                variant="contained"
                                disabled={props.thirdDisabled || props.thirdDisabled}>
                                {props.thirdTitle || "Abbrechen"}
                            </Button>
                        )}

                        {props.onSecond && (
                            <Button
                                data-cy={`${props.testId}-Button-Second`}
                                disableElevation
                                classes={{
                                    disabled: classes.disabledButton
                                }}
                                className={clsx(classes.button, (props.onThird
                                    ? classes.buttonPrimary
                                    : classes.buttonSecondary))}
                                onClick={props.onSecond}
                                variant={props.onThird ? "outlined" : "contained"}
                                disabled={props.disabled || props.secondDisabled}>
                                {props.secondTitle || "Abbrechen"}
                            </Button>
                        )}

                        {props.onFirst && (
                            <Button
                                data-cy={`${props.testId}-Button-First`}
                                disableElevation
                                classes={{
                                    disabled: classes.disabledButton
                                }}
                                className={clsx(classes.button, classes.buttonPrimary)}
                                onClick={props.onFirst}
                                variant="outlined"
                                disabled={props.disabled || props.firstDisabled}>
                                {props.firstTitle || "Fertig"}
                            </Button>
                        )}
                    </div>

                </DialogActions>
            )}

        </Dialog>
    );
};

export default PopupDialog;
